import React, { useState } from 'react';
import { Scale, Shield, TrendingUp, Target, Gavel, Award, CheckCircle, Clock, ChevronDown, ChevronUp, DollarSign, FileText } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const CopyrightLitigationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const strengths = [
    {
      icon: Scale,
      title: "Federal Court Experience",
      description: "Proven track record in federal copyright litigation with victories against major corporations and serial infringers."
    },
    {
      icon: DollarSign,
      title: "Maximum Damages Recovery",
      description: "Aggressive pursuit of statutory damages up to $150,000 per work, actual damages, profits, and attorney's fees."
    },
    {
      icon: Shield,
      title: "Injunctive Relief",
      description: "Obtain court orders to immediately stop ongoing infringement and prevent future violations."
    },
    {
      icon: Gavel,
      title: "Trial-Ready Attorneys",
      description: "Experienced litigators prepared to take cases to trial when necessary to protect your rights."
    },
    {
      icon: Award,
      title: "Strategic Case Management",
      description: "Comprehensive litigation strategy balancing aggressive enforcement with cost-effective resolution."
    },
    {
      icon: Target,
      title: "Expert Testimony",
      description: "Access to leading copyright experts and technical specialists to strengthen your case."
    }
  ];

  const caseTypes = [
    {
      title: "Commercial Infringement",
      description: "Businesses profiting from unauthorized use of your copyrighted works.",
      examples: ["Unauthorized product sales", "Commercial reproductions", "Competitor copying", "Brand exploitation"]
    },
    {
      title: "Digital Piracy",
      description: "Large-scale online distribution and illegal downloading of copyrighted content.",
      examples: ["File sharing networks", "Streaming sites", "Download portals", "App piracy"]
    },
    {
      title: "Willful Infringement",
      description: "Defendants who knowingly and intentionally violate your copyrights.",
      examples: ["Repeated violations", "Ignored cease & desist", "Deliberate copying", "Bad faith use"]
    },
    {
      title: "Secondary Liability",
      description: "Parties enabling, inducing, or contributing to copyright infringement.",
      examples: ["Platform liability", "Contributory infringement", "Vicarious liability", "ISP violations"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Case Evaluation & Strategy",
      description: "Thorough analysis of your case merits, potential damages, and development of a comprehensive litigation strategy.",
      timeline: "1-2 weeks"
    },
    {
      step: "2",
      title: "Filing & Discovery",
      description: "File the complaint in appropriate federal court and conduct thorough discovery including depositions and document requests.",
      timeline: "3-6 months"
    },
    {
      step: "3",
      title: "Resolution or Trial",
      description: "Pursue favorable settlement negotiations or proceed to trial with expert witness testimony and compelling arguments.",
      timeline: "6-18 months"
    }
  ];

  const packages = [
    {
      name: "Case Assessment",
      price: "$2,500",
      description: "Evaluate litigation viability",
      features: [
        "Comprehensive case review",
        "Copyright registration check",
        "Defendant research",
        "Damages analysis",
        "Litigation strategy memo",
        "Settlement prospects"
      ],
      popular: false
    },
    {
      name: "Standard Litigation",
      price: "$15,000+",
      description: "Full litigation services",
      features: [
        "Everything in Assessment",
        "Complaint drafting & filing",
        "Discovery management",
        "Motion practice",
        "Settlement negotiations",
        "Expert witness coordination",
        "Trial preparation"
      ],
      popular: true
    },
    {
      name: "Contingency Option",
      price: "30-40%",
      description: "No upfront costs",
      features: [
        "No upfront attorney fees",
        "Pay only if we win",
        "Percentage of recovery",
        "All litigation services",
        "Expert witness costs covered",
        "Full trial representation",
        "Appeals if necessary"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "When should I consider copyright litigation?",
      answer: "Consider litigation when: (1) cease and desist letters fail and infringement continues, (2) infringement causes significant financial harm, (3) the infringer refuses reasonable settlement, (4) you need injunctive relief to stop ongoing violations, or (5) statutory damages and attorney's fees make litigation economically viable. Litigation is particularly warranted for willful infringement, commercial exploitation, or cases with substantial damages potential exceeding $50,000."
    },
    {
      question: "How much does copyright litigation cost?",
      answer: "Costs vary significantly based on case complexity. Simple cases settled during discovery might cost $15,000-$50,000. Cases proceeding through trial can cost $100,000-$500,000+. However, copyright law allows recovery of attorney's fees if you prevail, making litigation economically viable against well-funded defendants. We offer contingency arrangements (30-40% of recovery) for strong cases where defendants have assets to satisfy judgments. Initial case assessment costs $2,500."
    },
    {
      question: "What damages can I recover in copyright litigation?",
      answer: "With registered copyright, you can elect statutory damages of $750-$30,000 per work ($150,000 for willful infringement) without proving actual harm. Alternatively, you can recover actual damages (your losses) plus the infringer's profits attributable to the infringement. You can also recover attorney's fees and costs if you prevail. Injunctive relief stops ongoing infringement. In exceptional cases involving multiple works, total recovery can reach millions of dollars."
    },
    {
      question: "How long does copyright litigation take?",
      answer: "Timeline varies by case complexity and court congestion. Simple cases may settle within 3-6 months. Cases proceeding through discovery typically take 12-18 months to trial. Complex cases involving multiple parties or technical issues can extend 2-3 years. Preliminary injunctions can be obtained in weeks to months for urgent matters. Appeals add 12-18 months. Most cases (85-90%) settle before trial, often during discovery phase when both sides assess case strength."
    },
    {
      question: "Do I need copyright registration before filing a lawsuit?",
      answer: "Yes, U.S. law requires registration before filing copyright infringement lawsuits. If you haven't registered, we can file expedited registration (approved in 5-7 business days) before initiating litigation. Registration within 3 months of publication or before infringement begins qualifies you for statutory damages and attorney's fees—critical benefits for successful litigation. Without timely registration, you're limited to actual damages and profits, making many cases economically impractical."
    },
    {
      question: "What if the infringer has no money or assets?",
      answer: "Defendant's financial condition is crucial to case viability. We investigate defendant assets, business operations, insurance coverage, and parent companies before recommending litigation. Judgments against insolvent defendants may be uncollectible. However, injunctive relief alone may be valuable to stop competition. Some defendants hide assets or operate through shell companies—we have experience piercing corporate veils and identifying true parties. Contingency arrangements require defendant solvency assessment."
    },
    {
      question: "Can I sue for copyright infringement in state court?",
      answer: "No, federal law grants exclusive jurisdiction over copyright cases to federal district courts. Copyright litigation must be filed in U.S. District Court, typically in the district where the defendant resides or where infringement occurred. This federal jurisdiction ensures consistent copyright law application nationwide and access to specialized judges experienced in intellectual property matters. Some related state law claims (unfair competition, breach of contract) may be joined with federal copyright claims."
    },
    {
      question: "What is willful infringement and why does it matter?",
      answer: "Willful infringement occurs when the defendant knowingly violated your copyright or acted with reckless disregard of your rights. Proof of willfulness increases maximum statutory damages from $30,000 to $150,000 per work—a 5x multiplier. Evidence includes: prior cease and desist letters, copyright notices on your work, defendant's industry knowledge, or deliberate efforts to conceal copying. Willfulness findings also support punitive approaches and increased attorney's fees awards."
    },
    {
      question: "How strong does my case need to be to file a lawsuit?",
      answer: "You need: (1) valid copyright ownership (registration certificate), (2) evidence of defendant's access to your work, (3) substantial similarity between your work and defendant's, and (4) copying of protected expression (not just ideas). The stronger these elements, the better settlement leverage and trial prospects. We assess case strength during initial consultation—cases with 70%+ win probability are litigation-ready. Weaker cases may benefit from additional evidence gathering or may not be economically viable."
    },
    {
      question: "What happens if I lose my copyright lawsuit?",
      answer: "If you lose on merits, you typically pay your own attorney's fees and costs (defendant rarely recovers fees in copyright cases unless your claims were objectively unreasonable or frivolous). You cannot refile the same claims (res judicata). However, losing doesn't prevent future claims for different infringement. Risk of loss is why thorough case assessment is critical. Strong cases often settle because defendants recognize their exposure. We only recommend litigation when case strength and potential recovery justify the risks and costs."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="Copyright Litigation Services - Federal Court Representation"
        description="Federal copyright litigation to stop infringement and recover damages. Experienced trial attorneys."
        keywords="copyright litigation, federal court, copyright lawsuit, infringement damages, statutory damages, attorney fees"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Scale className="w-4 h-4" />
                <span>Federal Copyright Litigation</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Aggressive Copyright Litigation to Protect Your Rights
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                When negotiations fail, federal litigation may be your only option. Our trial-ready attorneys pursue maximum damages and injunctive relief to stop infringement and recover your losses.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  Evaluate Your Case
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Litigator
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Contingency Options</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Trial Experience</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why Litigate?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Maximum Damages</strong>
                    <span className="text-gray-300">Statutory damages up to $150,000 per work without proving actual harm</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Stop Infringement</strong>
                    <span className="text-gray-300">Court orders forcing immediate cessation of all infringing activity</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Recover Legal Costs</strong>
                    <span className="text-gray-300">Attorney's fees and litigation costs paid by losing infringers</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Litigation Strengths */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Litigation Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven federal court experience with track record of substantial verdicts and settlements
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {strengths.map((strength, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent hover:-translate-y-2 hover:scale-105 cursor-pointer">
                <strength.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white mb-4 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white mb-3 transition-colors duration-500">{strength.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 leading-relaxed transition-colors duration-500">{strength.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Types of Copyright Cases We Handle
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive litigation experience across all copyright infringement categories
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {caseTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{type.title}</h3>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <div className="space-y-2">
                  {type.examples.map((example, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-700">
                      <div className="w-1.5 h-1.5 bg-[#FF035B] rounded-full"></div>
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Copyright Litigation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic approach from case evaluation through trial
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="Copyright Litigation Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about copyright litigation
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Fight for Your Rights?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Don't let infringers profit from your creative work. Our litigation team is ready to pursue maximum damages and stop violations permanently.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              Schedule Case Evaluation
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Speak with Litigator
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default CopyrightLitigationPage;
